import { motion } from "framer-motion";
import { useState } from "react";
import { CHANNEL_PARTNERS } from "../../lib/imageSlots";
import { useContent } from "../../lib/ContentContext";

/**
 * Brand-logo wall of channel / OTT partners.
 * Logos resolve via Google's favicon service (128px, high quality) with
 * DuckDuckGo's icon service and a monogram as graceful fallbacks.
 */
function LogoTile({ name, domain, i }) {
  const [stage, setStage] = useState(0); // 0=google 1=ddg 2=monogram
  const sources = [
    `https://www.google.com/s2/favicons?sz=128&domain=${domain}`,
    `https://icons.duckduckgo.com/ip3/${domain}.ico`,
  ];
  const monogram = name.replace(/\s?TV$/, "").trim().charAt(0).toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (i % 7) * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex items-center gap-3 md:gap-3.5 w-[44%] sm:w-[30%] md:w-[22%] lg:w-[17.5%] xl:w-[12.6%] rounded-2xl px-4 py-4 md:py-5 bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.05] hover:border-white/15 transition-all duration-500"
      data-testid={`channel-logo-${domain}`}
    >
      {/* Logo chip — soft frosted glass, no hard white box */}
      <div className="relative shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-xl bg-white/[0.08] backdrop-blur-md border border-white/10 flex items-center justify-center overflow-hidden group-hover:scale-105 transition-transform duration-500">
        {stage < sources.length ? (
          <img
            src={sources[stage]}
            alt={name}
            onError={() => setStage((s) => s + 1)}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-7 h-7 md:w-8 md:h-8 object-contain"
          />
        ) : (
          <span className="font-display text-white text-lg font-semibold">{monogram}</span>
        )}
      </div>

      <span className="font-display text-white/90 tracking-tight text-sm md:text-[0.95rem] leading-tight">
        {name}
      </span>
    </motion.div>
  );
}

export default function ChannelLogos() {
  const { getText } = useContent();
  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden bg-black"
      data-testid="channel-logos-section"
    >
      {/* Ambient */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[280px] rounded-full bg-[#e63946] opacity-[0.08] blur-[160px]" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="flex justify-center">
            <div className="tag-glass">
              <span className="dot" /> {getText("logos.tag")}
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display text-white text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl mx-auto"
          >
            {getText("logos.headline_a")}<span className="italic-accent text-grad-crimson">{getText("logos.headline_b")}</span>{getText("logos.headline_c")}
          </motion.h2>
          <p className="mt-5 text-white/72 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            {getText("logos.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {CHANNEL_PARTNERS.map((p, i) => (
            <LogoTile key={p.domain} name={p.name} domain={p.domain} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
